import { TableRow } from '@sam/components';
import { Dispatch, SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  BonusPayout,
  MonthlyReceipt,
  SchambeckUser,
  TimeConfirmation,
  YearlyTimeOverview,
} from 'shared';
import dayjs from 'shared/src/tools/Dayjs';
import { uid } from 'uid';
import ReceiptDetailBox from '../../components/receiptDetailBox/ReceiptDetailBox';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { getUserName } from '../user/User.utils';

/**
 * Util method to convert monthlyReceipts to tableEntries, to display them on the monthly overview
 * @param receipts monthlyReceipts to convert into TableEntries
 * @param users users that are included in the monthlyReceipt to get totalIst and totalSoll data from the last month
 * @param year of the receipts
 * @param setMonthlyOverviews dispatch action to set the monthlyReceipts
 * @returns array of TableRows
 */
export const convertMonthlyReceiptIntoTableEntry = (
  receipts: MonthlyReceipt[],
  users: SchambeckUser[],
  year: number,
  setMonthlyOverviews: Dispatch<SetStateAction<MonthlyReceipt[]>>,
  downloadTimeSheet: (user: SchambeckUser) => void,
  isHrEmployee: boolean,
  navigate: NavigateFunction,
): TableRow[] => {
  return receipts.map((receipt) => {
    const user: SchambeckUser | undefined = users.find((user) => user.id === receipt.userId);

    const yearlyTimes: Map<number, YearlyTimeOverview> | undefined =
      user?.contractInformation?.yearlyTimes;

    let overTimeAvailable: number;
    let overtimeLeft: number;
    const totalIst: number | undefined = yearlyTimes?.get(year)?.totalIst;
    const totalSoll: number | undefined = yearlyTimes?.get(year)?.totalSoll;
    if (!user?.contractInformation.bonusPayments) {
      overTimeAvailable = 0;
      overtimeLeft = 0;
    } else if (totalIst && totalSoll) {
      overtimeLeft = receipt.istHours - receipt.sollHours - receipt.payedOutSaldo / 60;
      overTimeAvailable = totalIst - totalSoll + receipt.istHours - receipt.sollHours / 60;
    } else {
      overTimeAvailable = receipt.istHours - receipt.sollHours;
      overtimeLeft = receipt.istHours - receipt.sollHours - receipt.payedOutSaldo / 60;
    }
    const confirmation: TimeConfirmation | undefined = user?.contractInformation?.yearlyTimes
      ?.get(year)
      ?.confirmations.sort((confirmationA, confirmationB) =>
        dayjs(confirmationA.confirmationDate).isAfter(confirmationB.confirmationDate, 'second')
          ? -1
          : 1,
      )
      .find((confirmation) => confirmation.month === receipt.month);

    return {
      id: receipt.id,
      content: [
        <IconComponent type={receipt.completed ? IconType.CHECKED : IconType.ERROR} width={25} />,
        user ? getUserName(user) : '',
        `${receipt.istHours}/${receipt.sollHours}`,
        overTimeAvailable.toString(),
        (receipt.payedOutSaldo,
        receipt.holidayBonus100 +
          receipt.holidayBonus150 +
          receipt.nightBonus +
          receipt.saturdayBonus +
          receipt.sundayBonus).toString() + ' h',
        overtimeLeft.toString(),
      ],
      actions: [
        <IconComponent
          type={IconType.TIME}
          className={`clickable ${confirmation?.confirmationDate ? 'valid' : 'invalid'}`}
          onClick={() =>
            user &&
            navigate('/timekeeping', {
              state: { userId: user.id },
            })
          }
        />,
        receipt.softLocked || receipt.completed ? (
          <IconComponent
            type={IconType.LOCK_CLOSED}
            className={
              isHrEmployee ? (receipt.completed ? 'clickable completed' : 'clickable') : 'disabled'
            }
            onClick={() => {
              if (!isHrEmployee) return;
              setMonthlyOverviews((prev) =>
                prev.map((receiptEntry) =>
                  receiptEntry.id === receipt.id
                    ? { ...receipt, softLocked: false, completed: false }
                    : receiptEntry,
                ),
              );
            }}
          />
        ) : (
          <IconComponent
            type={IconType.LOCK_OPEN}
            className="clickable"
            onClick={() => {
              setMonthlyOverviews((prev) =>
                prev.map((receiptEntry) =>
                  receiptEntry.id === receipt.id ? { ...receipt, softLocked: true } : receiptEntry,
                ),
              );
            }}
          />
        ),
        <IconComponent
          type={IconType.USER}
          className="clickable"
          onClick={() => navigate('/user/create', { state: { user } })}
        />,
        <IconComponent
          type={IconType.PREVIEW}
          className={`clickable ${confirmation?.confirmationDate ? 'valid' : ''}`}
          onClick={() => user && downloadTimeSheet(user)}
        />,
        confirmation?.comment ? (
          <IconComponent
            type={IconType.EXCLAMATION}
            title={confirmation.comment}
            className={`red-icon clickable`}
          />
        ) : (
          <></>
        ),
      ],
      children: (
        <ReceiptDetailBox
          receiptUser={user}
          monthlyReceipt={receipt}
          monthlyReceipts={receipts}
          setMonthlyOverviews={setMonthlyOverviews}
        />
      ),
    };
  });
};

/**
 * Util method to generate an empty BonusPayout
 * @returns created BonusPayout with code generated id
 */
export const generateEmptyBonusPayout = (override?: Partial<BonusPayout>): BonusPayout => ({
  id: uid(),
  description: '',
  amount: 0,
  reduction: false,
  ...override,
});
